import { Capacitor, registerPlugin } from "@capacitor/core";
import type { ViewerOpenSource } from "../hooks/useViewerOpenAnalytics";
import type { ViewerContext } from "../shared/interfaces";
import { nativeScopeRequestProof } from "./nativeAdmittedScope";

/**
 * Bridge to the native PDFKit viewer module (iOS Capacitor shell, Release 1E).
 * Native side: ios/App/App/NativeViewerPlugin.swift.
 */
export interface NativeViewerOptions {
    url: string;
    /** Tenant slug — cache keys are org-scoped (multi-tenant safety). */
    orgSlug: string;
    /** Stable file id — the cache key; never the SAS URL, which rotates. */
    fileId: string;
    title?: string;
}

/**
 * Options for openViewer: the native viewer fetches section groups + connections
 * itself via ViewerApi using the native Stytch session JWT, and downloads each
 * sheet's file via NativeFileCache keyed {orgSlug}/{fileId} — the web layer no
 * longer resolves a SAS URL up front.
 */
export interface NativeViewerOpenOptions {
    documentId: string;
    /** Omitted for single-file documents (no section groups) — native falls back to fetchDocument. */
    sectionGroupId?: string;
    /** Tenant slug — cache keys are org-scoped (multi-tenant safety). */
    orgSlug: string;
    apiOrigin: string;
    title?: string;
    /**
     * The document's `document_type` (e.g. `"CSI"` for specifications), so the native
     * star button attributes to the matching `StarredSheetsStore` namespace (Sheets vs
     * Specifications) instead of always defaulting to the Sheets gallery's list. Omit
     * when the caller has no document in hand — native falls back to the Sheets namespace.
     */
    documentType?: string;
    /** 1-based page to land on, from the first reference/search highlight bounds. */
    page?: number;
    /** Normalized (0..1, top-left origin) box to flash on arrival — same bounds shape as ApiBounds, minus `page`. */
    highlight?: { xMin: number; yMin: number; xMax: number; yMax: number };
    /**
     * Raw-file fallback for opens whose `documentId` may not resolve on the AI
     * service (conversation attachments, citation files that reference a
     * document's SOURCE file rather than a document row): when the native
     * document fetch 404s, the viewer renders this file directly instead of
     * erroring. `fileId` is the stable cache key; `fileUrl` the (rotating) SAS URL.
     */
    fileId?: string;
    fileUrl?: string;
    /**
     * How the page opened it, for the native viewer's one `viewer_open`
     * (`ViewerOpenContext` in NativeViewerPlugin.swift): the args' explicit context
     * and its source. Omitted when the args name no context: native counts a direct open.
     */
    viewerContext?: ViewerContext;
    viewerSource?: ViewerOpenSource;
}

interface NativeViewerPlugin {
    /** Presents the full-screen native viewer; resolves when it is dismissed. */
    openViewer(options: NativeViewerOpenOptions): Promise<void>;
    /** Ensures the file is in the native cache and returns its on-device path. */
    getCachedFile(options: Omit<NativeViewerOptions, "title">): Promise<{ path: string }>;
}

const NativeViewer = registerPlugin<NativeViewerPlugin>("NativeViewer");

export function isNativeViewerAvailable(): boolean {
    return Capacitor.getPlatform() === "ios";
}

export function openNativeViewer(options: NativeViewerOpenOptions): Promise<void> {
    return NativeViewer.openViewer({ ...options, ...nativeScopeRequestProof() });
}

/**
 * Robust replacement for OPFS inside the WebView: the native layer downloads
 * and caches the file on-device (shared with the PDFKit viewer's cache), and
 * the bytes are read back over convertFileSrc — never base64 across the bridge.
 */
export async function fetchViaNativeCache(
    options: Omit<NativeViewerOptions, "title">,
): Promise<Response> {
    const proof = nativeScopeRequestProof();
    const { path } = await NativeViewer.getCachedFile({ ...options, ...proof });
    if (proof !== nativeScopeRequestProof()) throw new Error("Native cached file scope changed");
    const response = await fetch(Capacitor.convertFileSrc(path));
    if (proof !== nativeScopeRequestProof()) throw new Error("Native cached file scope changed");
    return response;
}
